import { useQuery, useReactiveVar } from '@apollo/client';
import { addDays } from 'date-fns';
import { useTranslation } from 'gatsby-plugin-react-i18next';
import React, { Fragment, useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { FORM_PAGES } from '../../../components/Form/types';
import FormRadioDateAndTime from '../../../components/FormRadio/DateAndTime/FormRadio';
import {
  checkIfSlotsCompleted,
  findBookingDate,
  getBiggestAndSmallestDates,
  getBookingDates,
  getUTCTime,
  isProductOtherSlotType,
  setSlotFieldBySession,
  splitBookingDatesIntoMonths,
  toDateString
} from '../../../components/FormRadio/helpers';
import { CloseButton, NewSlotGroupButton, SessionsContainer, SessionsWrapper, SlotGroupButton } from '../../../components/FormRadio/styled';
import { FORM_RADIO_COMPONENT_TYPES, FORM_RADIO_TYPES } from '../../../components/FormRadio/types';
import Animation from '../../../components/Shared/Animation/Animation';
import { Line } from '../../../components/Shared/Shared';
import useAnimatedPages from '../../../hooks/useAnimatedPages';
import useUserProfile from '../../../hooks/useUserProfile';
import MinusSvg from '../../../images/minus.svg';
import PlusSvg from '../../../images/plus.svg';
import { vars } from '../../../reactive';
import { setFormPage } from '../../../reactive/actions';
import { Slot } from '../../../reactive/main';
import { AnalyticsEvents, trackEvent } from '../../../utils/analytics';
import { GetSlots } from '../../../utils/queries';

type SlotsAvailability = {
  availableSlots: {
    timestamp: string;
    available: number;
    hasMaxSlotsPerDay: boolean;
    isCheckoutOnly: boolean;
    mainSlotAvailablity: number;
  }[][];
  timesByDay: { day: string; times: string[]; available: number }[];
};

export type Slots = SlotsAvailability & {
  usedSchedules: { id: string; name: string; time: string; available: number; booking_type: string; slots_availability: SlotsAvailability; BusUserId: string | null }[];
};

const SLOTS_LIMIT = 30;
const OTHER_SLOT_LIMIT = 180;
const MULTI_DAY_SLOTS_LIMIT = 360;

const FormBookingDateSlots = ({
  orderGroupIndex,
  selectedSlots,
  slots,
  loading,
  children,
  onLoadMore,
  hasMoreSlots
}: {
  orderGroupIndex: number;
  selectedSlots: Slot[];
  slots: Slots;
  loading: boolean;
  children?: React.ReactNode;
  onLoadMore: () => void;
  hasMoreSlots: boolean;
}) => {
  const { t } = useTranslation();
  const dates = slots?.availableSlots?.map(slots => new Date(slots[0].timestamp));
  const [getProfile, { loading: userProfileLoading }] = useUserProfile(true);
  const product = vars.selectedProduct()!;
  const bookingDays = slots?.timesByDay || [];
  const recurrenceType = useReactiveVar(vars.recurrenceType);
  const isRecurring = recurrenceType === 'recurring' && product.allows_repeated_orders;
  const sessions = Array.from(Array(product?.sessions).keys());
  const { currentPage, decreaseCurrentPage, increaseCurrentPage } = useAnimatedPages(isRecurring ? 0 : 1);
  const multiDayProduct = product?.booking_type === 'MULTI_DAY';
  const otherSlotType = isProductOtherSlotType() || slots?.usedSchedules?.every(schedule => schedule.booking_type === 'ONE_OFF');
  const dateType = isRecurring ? FORM_RADIO_TYPES.START_DATE : multiDayProduct ? FORM_RADIO_TYPES.DATE_UNTIL : FORM_RADIO_TYPES.DATE;
  const daysType = FORM_RADIO_TYPES.DAYS;
  const timeType = FORM_RADIO_TYPES.TIME;
  const busUserTimeType = FORM_RADIO_TYPES.BUS_USER_TIME;
  const daysComponentType = FORM_RADIO_COMPONENT_TYPES[daysType];
  const dateComponentType = FORM_RADIO_COMPONENT_TYPES[dateType];
  const timeComponentType = FORM_RADIO_COMPONENT_TYPES[timeType];
  const title = t(dateComponentType.title, {
    booking: product?.name || t('yourBooking')
  });

  const busUserTimeComponentType = FORM_RADIO_COMPONENT_TYPES[busUserTimeType];
  const defaultDatesValue =
    selectedSlots.map(slot => {
      if (multiDayProduct) {
        return [slot.date, slot.startDate, slot.dateUntil].filter(Boolean)?.map(date => toDateString(new Date(String(date?.fullDate)) || ''));
      }
      if (slot.date) {
        return toDateString(new Date(String(slot.date?.fullDate)));
      }
      if (slot.startDate) {
        return toDateString(new Date(String(slot.startDate?.fullDate)));
      }
    }) || sessions.map(() => []);

  const defaultDaysValue = selectedSlots.map(slot => slot.days) || sessions.map(() => []);
  const defaultTimesValue = selectedSlots.map(slot => slot.time) || sessions.map(() => '');
  const defaultBusUserTimesValue = selectedSlots.map(slot => slot.BusUserId) || sessions.map(() => '');

  const isBreakdownAllowed = !!product?.allow_availability_schedule_breakdown && slots?.usedSchedules?.every(schedule => !!schedule.BusUserId);

  const formOptions = useForm({
    defaultValues: {
      [dateComponentType.registerAs]: defaultDatesValue,
      [daysComponentType.registerAs]: defaultDaysValue,
      [timeComponentType.registerAs]: defaultTimesValue,
      [busUserTimeComponentType.registerAs]: defaultBusUserTimesValue
    },
    shouldUnregister: false
  });

  const { watch, setValue } = formOptions;
  const bookingDates = getBookingDates(dates, slots, { removeDaysWithoutSlots: otherSlotType });
  const bookings = splitBookingDatesIntoMonths(bookingDates);
  const sessionsState = useState((selectedSlots?.length || 1) - 1);
  const [currentSession] = sessionsState;

  const watchedDates = watch(dateComponentType.registerAs);

  const watchedDays = watch(daysComponentType.registerAs);

  const watchedTimes = watch(timeComponentType.registerAs);

  const watchedBusUserTimes = watch(busUserTimeComponentType.registerAs);

  useEffect(() => {
    if (!watchedDates?.[currentSession]?.length) {
      return;
    }

    if (dateType === FORM_RADIO_TYPES.DATE) {
      const newSelectedSlot = findBookingDate(bookings, watchedDates[currentSession] as string | Date);
      const slots = newSelectedSlot?.slots || [];
      setSlotFieldBySession({
        orderGroupIndex,
        session: currentSession,
        date: newSelectedSlot
      });

      setValue(timeComponentType.registerAs, []);

      if (slots.length === 1 && !isBreakdownAllowed) {
        const time = getUTCTime(slots[0]);

        setSlotFieldBySession({
          orderGroupIndex,
          session: currentSession,
          time
        });
        setValue(timeComponentType.registerAs, [time]);
      }
    }

    if (dateType === FORM_RADIO_TYPES.START_DATE) {
      const newSelectedSlot = findBookingDate(bookings, watchedDates[currentSession] as string | Date);
      const slots = newSelectedSlot?.slots || [];
      setSlotFieldBySession({
        orderGroupIndex,
        session: currentSession,
        startDate: newSelectedSlot
      });
      setValue(timeComponentType.registerAs, []);
      if (slots.length === 1 && !isBreakdownAllowed) {
        const time = getUTCTime(slots[0]);
        setSlotFieldBySession({
          orderGroupIndex,
          session: currentSession,
          time
        });
        setValue(timeComponentType.registerAs, [time]);
      }
    }

    if (dateType === FORM_RADIO_TYPES.DATE_UNTIL) {
      const { biggestDate, smallestDate } = getBiggestAndSmallestDates((watchedDates[currentSession] as (string | Date)[]).filter(Boolean));
      const newSelectedSlotDate = findBookingDate(bookings, smallestDate);
      const newSelectedSlotDateUntil = findBookingDate(bookings, biggestDate);
      setSlotFieldBySession({
        orderGroupIndex,
        session: currentSession,
        date: newSelectedSlotDate,
        dateUntil: newSelectedSlotDateUntil
      });
    }
  }, [JSON.stringify(watchedDates)]);

  useEffect(() => {
    if (!watchedDays[currentSession]?.length) {
      return;
    }

    const days = watchedDays[currentSession];
    setSlotFieldBySession({
      orderGroupIndex,
      session: currentSession,
      days
    });
  }, [JSON.stringify(watchedDays)]);

  useEffect(() => {
    if (!watchedTimes[currentSession]) {
      return;
    }

    const time = watchedTimes[currentSession];
    setSlotFieldBySession({
      orderGroupIndex,
      session: currentSession,
      time
    });
    setValue(timeComponentType.registerAs, [time]);
    setValue(busUserTimeComponentType.registerAs, []);
  }, [JSON.stringify(watchedTimes)]);

  useEffect(() => {
    if (!watchedBusUserTimes[currentSession]) {
      return;
    }

    const BusUserId = watchedBusUserTimes[currentSession];
    setSlotFieldBySession({
      orderGroupIndex,
      session: currentSession,
      BusUserId
    });
    setValue(busUserTimeComponentType.registerAs, [BusUserId]);
  }, [JSON.stringify(watchedBusUserTimes)]);

  const onSubmit = (form: Record<string, (string | Date)[][] | (string | Date)[]>) => {
    const selectedOrderGroupSlots = vars.selectedOrderGroupSlots();
    const allRequiredSlotsFilled = selectedOrderGroupSlots.every(
      ({ slots: selectedSlots }) =>
        !!selectedSlots.length &&
        selectedSlots.every(slot => {
          if (currentPage === 0 && isRecurring) {
            return slot.days?.length;
          }
          if (dateType === FORM_RADIO_TYPES.DATE_UNTIL) {
            return slot.date && slot.dateUntil;
          }

          if (dateType === FORM_RADIO_TYPES.START_DATE) {
            return slot.startDate;
          }

          return slot.date && slot.time;
        })
    );

    const breakdownSlotsFilled = !isBreakdownAllowed || selectedOrderGroupSlots.every(({ slots }) => slots.every(slot => !!slot.BusUserId));

    const allSessionsFilled = allRequiredSlotsFilled && selectedSlots.length === product?.sessions && breakdownSlotsFilled;
    if (!allSessionsFilled) {
      return;
    }

    trackEvent(AnalyticsEvents.DATE_TIME_SELECTED);

    if (currentPage === 0) {
      const days = form[daysComponentType.registerAs][currentSession] as string[];
      setSlotFieldBySession({ session: currentSession, days, orderGroupIndex });
      increaseCurrentPage();
      return;
    }

    if (isRecurring && currentPage === 1) {
      setFormPage(FORM_PAGES.BOOKING_CALENDAR_DATE_AND_TIME_REPEATITION);
      return;
    }

    if (vars.isLoggedIn()) {
      getProfile();
    } else {
      setFormPage(FORM_PAGES.CREATE_ACCOUNT_EMAIL);
    }
  };

  const previousPage = {
    onPrevious: () => {
      if (isRecurring && currentPage === 1) {
        decreaseCurrentPage();
        return;
      }
      let newPage = FORM_PAGES.PRODUCTS;

      if (Object.values(product?.customizations || {})?.length) {
        newPage = FORM_PAGES.PRODUCT_CUSTOMIZATIONS;
      }

      if (product?.allows_repeated_orders) {
        newPage = FORM_PAGES.BOOKING_CALENDAR_BOOKING_TYPE;
      }

      setFormPage(newPage);

      vars.selectedOrderGroupSlots([{ slots: [] }]);
    }
  };

  const usedSchedules = slots?.usedSchedules || [];

  return (
    <Animation animationKey={currentPage}>
      <>
        {currentPage === 0 && (
          <FormRadioDateAndTime
            dateType={daysType}
            timeType={timeType}
            bookings={bookings}
            formOptions={formOptions}
            onSubmit={onSubmit}
            loading={loading}
            sessions={sessions}
            sessionsState={sessionsState}
            previousPage={previousPage}
            nextLoading={userProfileLoading}
            watchedDates={watchedDates}
            days={bookingDays}
            selectedSlots={selectedSlots}
            orderGroupIndex={orderGroupIndex}
            children={children}
            busUserTimeType={busUserTimeType}
          />
        )}

        {currentPage === 1 && (
          <FormRadioDateAndTime
            title={title}
            dateType={dateType}
            timeType={timeType}
            bookings={bookings}
            formOptions={formOptions}
            usedSchedules={usedSchedules}
            onSubmit={onSubmit}
            loading={loading}
            sessions={sessions}
            sessionsState={sessionsState}
            previousPage={previousPage}
            nextLoading={userProfileLoading}
            watchedDates={watchedDates}
            days={bookingDays}
            selectedSlots={selectedSlots}
            orderGroupIndex={orderGroupIndex}
            children={children}
            busUserTimeType={busUserTimeType}
            onLoadMore={onLoadMore}
            hasMoreSlots={hasMoreSlots}
          />
        )}
      </>
    </Animation>
  );
};

const FormBookingDate = () => {
  const { t } = useTranslation();
  const selectedOrderGroupSlots = useReactiveVar(vars.selectedOrderGroupSlots);
  const [currentOrderGroupIndex, setCurrentOrderGroupIndex] = useState(0);
  const branch = vars.currentBranch();
  const product = vars.selectedProduct()!;
  const selectedCustomizations = vars.selectedCustomizations() || [];
  const isMultiDay = product?.booking_type === 'MULTI_DAY';
  const otherSlotType = isProductOtherSlotType();
  const [hasMoreSlots, setHasMoreSlots] = useState(true);
  const customizationsQuantity = 1;

  const customizationsFields =
    product && Object.keys(product?.customizations).length
      ? selectedCustomizations.map(customization => ({
          ...customization,
          quantity: customizationsQuantity
        }))
      : null;

  const {
    data: { getSlots: { slots = {} } = {} } = {},
    loading,
    fetchMore
  } = useQuery<{
    getSlots: {
      slots: Slots;
    };
  }>(GetSlots, {
    variables: {
      branchId: branch?.id,
      productId: product?.id,
      customizations: customizationsFields,
      slots_length: isMultiDay ? MULTI_DAY_SLOTS_LIMIT : otherSlotType ? OTHER_SLOT_LIMIT : SLOTS_LIMIT
    },
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
    nextFetchPolicy: 'cache-and-network',
    initialFetchPolicy: 'network-only',
    onCompleted: data => {
      const slots = data.getSlots.slots;
      if (!slots || !slots.availableSlots || slots.availableSlots.length === 0) {
        setHasMoreSlots(false);
      }
    }
  });

  const onLoadMore = useCallback(() => {
    if (loading) {
      return;
    }
    const availableSlots = (slots as Slots)?.availableSlots || [];
    const lastSlot = availableSlots[availableSlots.length - 1];
    const nextDate = addDays(lastSlot[0].timestamp, 1);
    nextDate.setUTCHours(0, 0, 0, 0);

    fetchMore({
      variables: {
        branchId: branch?.id,
        productId: product?.id,
        customizations: customizationsFields,
        slots_length: SLOTS_LIMIT,
        start_date: nextDate
      },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult) {
          return prev;
        }
        const newSlots = fetchMoreResult.getSlots.slots.availableSlots;
        if (!newSlots || newSlots.length === 0) {
          setHasMoreSlots(false);
          return prev;
        }
        const updatedSlots = [...(prev.getSlots.slots.availableSlots || []), ...newSlots];
        const newUsedSchedules = fetchMoreResult.getSlots.slots.usedSchedules || [];
        const updatedUsedSchedules = newUsedSchedules.map(schedule => {
          const existingSchedule = prev.getSlots.slots.usedSchedules.find(s => s.id === schedule.id);
          if (existingSchedule?.slots_availability) {
            return {
              ...existingSchedule,
              slots_availability: {
                ...existingSchedule.slots_availability,
                availableSlots: [...existingSchedule.slots_availability.availableSlots, ...schedule.slots_availability.availableSlots]
              }
            };
          }
          return schedule;
        });
        return {
          getSlots: {
            ...prev.getSlots,
            slots: {
              ...prev.getSlots.slots,
              availableSlots: updatedSlots,
              usedSchedules: updatedUsedSchedules
            }
          }
        };
      }
    });
  }, [loading, slots]);

  const completedOrderGroupSlots = selectedOrderGroupSlots.filter(({ slots: selectedSlots }) => checkIfSlotsCompleted(selectedSlots, product))?.length;
  const recurrenceType = vars.recurrenceType();
  const isRecurring = recurrenceType === 'recurring' && product.allows_repeated_orders;
  const showSlots = !isRecurring && product?.allows_bulk_orders && product?.max_bulk_orders > 1;
  const plusButtonDisabled = completedOrderGroupSlots < selectedOrderGroupSlots.length || product?.max_bulk_orders === completedOrderGroupSlots;
  const plusButtonShown = selectedOrderGroupSlots.length < product?.max_bulk_orders;

  const handleRemoveSlotGroup = useCallback(
    (e: React.MouseEvent<HTMLSpanElement, MouseEvent>, index: number) => {
      e.stopPropagation();
      const newSlots = [...selectedOrderGroupSlots];
      newSlots.splice(index, 1);
      vars.selectedOrderGroupSlots(newSlots);
      setCurrentOrderGroupIndex(prev => {
        if (prev === index || prev === 0) {
          return 0;
        }
        return prev - 1;
      });
    },
    [selectedOrderGroupSlots]
  );

  const handleAddSlotGroup = useCallback(() => {
    if (plusButtonDisabled) {
      return;
    }
    const newSlots = [...selectedOrderGroupSlots];
    newSlots.push({ slots: [] });
    vars.selectedOrderGroupSlots(newSlots);
    setCurrentOrderGroupIndex(selectedOrderGroupSlots.length);
  }, [selectedOrderGroupSlots]);

  return (
    <>
      {selectedOrderGroupSlots.map((groupSlots, index) => {
        const selected = currentOrderGroupIndex === index;
        return (
          <Fragment key={index}>
            {selected && (
              <FormBookingDateSlots
                selectedSlots={groupSlots?.slots || []}
                orderGroupIndex={currentOrderGroupIndex}
                slots={slots as Slots}
                loading={loading}
                onLoadMore={onLoadMore}
                hasMoreSlots={hasMoreSlots}
              >
                {showSlots && (
                  <>
                    <SessionsWrapper>
                      <SessionsContainer>
                        {selectedOrderGroupSlots.map((slot, index) => {
                          const selected = currentOrderGroupIndex === index;
                          const dates = slot.slots;
                          const completed = checkIfSlotsCompleted(dates, product);
                          const disabled = completedOrderGroupSlots < index;

                          return (
                            <SlotGroupButton
                              key={index}
                              onClick={() => setCurrentOrderGroupIndex(index)}
                              type="button"
                              selected={selected}
                              completed={completed}
                              disabled={disabled}
                              manySlots={selectedOrderGroupSlots.length > 1}
                            >
                              <span>
                                {t('slot')} {index + 1}
                              </span>
                              {selectedOrderGroupSlots.length > 1 && (
                                <CloseButton selected={selected} completed={completed} onClick={e => handleRemoveSlotGroup(e, index)}>
                                  <MinusSvg />
                                </CloseButton>
                              )}
                            </SlotGroupButton>
                          );
                        })}
                        {plusButtonShown && (
                          <NewSlotGroupButton type="button" onClick={handleAddSlotGroup} disabled={plusButtonDisabled}>
                            <div>
                              <PlusSvg />
                            </div>
                          </NewSlotGroupButton>
                        )}
                      </SessionsContainer>
                    </SessionsWrapper>
                    <Line />
                  </>
                )}
              </FormBookingDateSlots>
            )}
          </Fragment>
        );
      })}
    </>
  );
};

export default FormBookingDate;
